// src/pages/SavedSearchReports.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import {
    BellIcon,
    EnvelopeIcon,
    UserIcon,
    BuildingOfficeIcon,
    ClockIcon,
    ArrowPathIcon,
    FunnelIcon,
    DocumentTextIcon,
    EyeIcon,
    ClipboardDocumentIcon,
    PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const SavedSearchReports = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showTriggerModal, setShowTriggerModal] = useState(false);
    const [filters, setFilters] = useState({
        companyId: '',
        userId: ''
    });
    const [triggerParams, setTriggerParams] = useState({
        companyId: '',
        force: false
    });
    const [triggerLoading, setTriggerLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);

    useEffect(() => {
        loadLogs();
    }, [filters]);

    useEffect(() => {
        loadUsers();
        loadCompanies();
    }, []);

    const loadLogs = async () => {
        try {
            const filterVars = {
                companyId: filters.companyId || undefined,
                userId: filters.userId || undefined,
                limit: 200,
                offset: 0
            };
            const data = await executeQuery(queries.getSavedSearchReportLogs(), filterVars);
            setLogs(data.adminSavedSearchReportLogs?.items || []);
            setTotalCount(data.adminSavedSearchReportLogs?.totalCount || 0);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load report logs:', err);
        }
    };

    const loadUsers = async () => {
        try {
            const data = await executeQuery(queries.getUsers(), { limit: 500, offset: 0 });
            setUsers(data.adminUsers || []);
        } catch (err) {
            console.error('Failed to load users:', err);
        }
    };

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 500, offset: 0 });
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const handleTriggerReports = async () => {
        setTriggerLoading(true);
        try {
            const variables = {
                companyId: triggerParams.companyId || undefined,
                force: triggerParams.force || undefined
            };
            const result = await executeMutation(mutations.adminSendSavedSearchReports, variables);

            if (result.adminSendSavedSearchReports?.success) {
                setShowTriggerModal(false);
                setTriggerParams({ companyId: '', force: false });
                alert(`Reports job created! Job ID: ${result.adminSendSavedSearchReports.jobId || 'N/A'}`);
                setTimeout(loadLogs, 3000);
            } else {
                alert('Error: ' + (result.adminSendSavedSearchReports?.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Failed to trigger reports:', err);
            alert('Failed to trigger reports: ' + err.message);
        } finally {
            setTriggerLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    const getTimeAgo = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const getUserById = (userId) => users.find(u => u.id === userId);
    const getCompanyById = (companyId) => companies.find(c => c.id === companyId);

    const copyToClipboard = async (text, e) => {
        if (e) e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const columns = [
        {
            key: 'sentAt',
            title: 'Sent',
            sortable: true,
            render: (sentAt) => (
                <div>
                    <div className="text-white">{formatDate(sentAt)}</div>
                    <div className="text-gray-400 text-xs">{getTimeAgo(sentAt)}</div>
                </div>
            )
        },
        {
            key: 'recipientEmail',
            title: 'Recipient',
            render: (email, log) => {
                const user = getUserById(log.userId);
                return (
                    <div className="flex items-center space-x-2">
                        <div className="h-7 w-7 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                            {user?.avatar ? (
                                <img src={user.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                            ) : (
                                <UserIcon className="h-4 w-4 text-gray-400" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-white text-sm truncate">{email}</div>
                            {user && (
                                <div className="text-gray-400 text-xs truncate">
                                    {user.firstName} {user.lastName}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'companyId',
            title: 'Company',
            render: (companyId) => {
                const company = getCompanyById(companyId);
                return company ? (
                    <div className="text-white text-sm">{company.name}</div>
                ) : (
                    <span className="text-gray-500 text-xs font-mono">{companyId?.slice(0, 8)}...</span>
                );
            }
        },
        {
            key: 'emailMode',
            title: 'Mode',
            render: (mode) => (
                <StatusBadge status={mode?.toLowerCase().replace(/_/g, ' ')} />
            )
        },
        {
            key: 'tendersIncluded',
            title: 'Content',
            render: (tenders, log) => (
                <div className="text-sm">
                    <div className="text-white">
                        {tenders} tender{tenders !== 1 ? 's' : ''}
                    </div>
                    <div className="text-gray-400 text-xs">
                        {log.searchesIncluded} search{log.searchesIncluded !== 1 ? 'es' : ''}
                    </div>
                </div>
            )
        },
        {
            key: 'savedSearchName',
            title: 'Saved Search',
            render: (name) => (
                <div className="text-white text-sm truncate max-w-[160px]" title={name}>
                    {name || <span className="text-gray-500 italic">-</span>}
                </div>
            )
        },
        {
            key: 'jobId',
            title: 'Job',
            render: (jobId) => jobId ? (
                <div className="flex items-center space-x-1">
                    <span className="text-blue-700 font-mono text-xs">{jobId.slice(0, 8)}...</span>
                    <button
                        onClick={(e) => copyToClipboard(jobId, e)}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="Copy job ID"
                    >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : (
                <span className="text-gray-500">-</span>
            )
        },
        {
            key: 'actions',
            title: '',
            render: (_, log) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(log);
                        setShowDetailModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-primary"
                    title="View details"
                >
                    <EyeIcon className="h-4 w-4" />
                </button>
            )
        }
    ];

    // Stats
    const uniqueRecipients = new Set(logs.map(l => l.recipientEmail)).size;
    const totalTenders = logs.reduce((sum, l) => sum + (l.tendersIncluded || 0), 0);
    const recentReports = logs.filter(l =>
        new Date(l.sentAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    ).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <BellIcon className="h-8 w-8 text-primary" />
                    <div>
                        <h1 className="text-2xl font-bold text-white">Saved Search Reports</h1>
                        <p className="text-gray-400 text-sm">Monitor and debug customer email notifications</p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={loadLogs}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 flex items-center space-x-2"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={() => setShowTriggerModal(true)}
                        className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                    >
                        <PaperAirplaneIcon className="h-4 w-4" />
                        <span>Send Reports</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-blue-500 bg-opacity-20 p-2 mr-3">
                            <EnvelopeIcon className="h-5 w-5 text-blue-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Total Reports</p>
                            <p className="text-xl font-semibold text-white">{totalCount}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-green-500 bg-opacity-20 p-2 mr-3">
                            <UserIcon className="h-5 w-5 text-green-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Unique Recipients</p>
                            <p className="text-xl font-semibold text-green-700">{uniqueRecipients}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-purple-500 bg-opacity-20 p-2 mr-3">
                            <DocumentTextIcon className="h-5 w-5 text-purple-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Tenders Shared</p>
                            <p className="text-xl font-semibold text-purple-700">{totalTenders}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-yellow-500 bg-opacity-20 p-2 mr-3">
                            <ClockIcon className="h-5 w-5 text-yellow-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Last 24 Hours</p>
                            <p className="text-xl font-semibold text-yellow-700">{recentReports}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-400 text-sm">Filters:</span>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <select
                            value={filters.companyId}
                            onChange={(e) => setFilters(prev => ({ ...prev, companyId: e.target.value, userId: '' }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Companies</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <select
                            value={filters.userId}
                            onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Users</option>
                            {users
                                .filter(u => !filters.companyId || u.companyId === filters.companyId)
                                .map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.email} ({user.firstName} {user.lastName})
                                    </option>
                                ))}
                        </select>
                    </div>
                    <button
                        onClick={() => setFilters({ companyId: '', userId: '' })}
                        className="px-3 py-1.5 bg-gray-700 text-white hover:bg-gray-600 text-sm"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Report Logs Table */}
            <DataTable
                columns={columns}
                data={logs}
                loading={loading}
                emptyMessage="No report logs found"
                onRowClick={(log) => {
                    setSelectedLog(log);
                    setShowDetailModal(true);
                }}
            />

            {/* Last Refresh */}
            {lastRefresh && (
                <div className="text-sm text-gray-500 text-center">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                </div>
            )}

            {/* Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedLog(null);
                }}
                title="Report Details"
                size="lg"
            >
                {selectedLog && (
                    <ReportDetailPanel
                        log={selectedLog}
                        user={getUserById(selectedLog.userId)}
                        company={getCompanyById(selectedLog.companyId)}
                        formatDate={formatDate}
                    />
                )}
            </Modal>

            {/* Trigger Modal */}
            <Modal
                isOpen={showTriggerModal}
                onClose={() => setShowTriggerModal(false)}
                title="Send Saved Search Reports"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-gray-400 text-sm">
                        Trigger email reports for all saved searches with new matching tenders.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Company (optional)
                        </label>
                        <select
                            value={triggerParams.companyId}
                            onChange={(e) => setTriggerParams(prev => ({ ...prev, companyId: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Companies</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <label className="flex items-center space-x-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={triggerParams.force}
                            onChange={(e) => setTriggerParams(prev => ({ ...prev, force: e.target.checked }))}
                            className="bg-gray-700 border-gray-600 text-primary focus:ring-primary"
                        />
                        <span>Force send (skip last-sent timing checks)</span>
                    </label>

                    <div className="flex justify-end space-x-2 pt-4 border-t border-gray-700">
                        <button
                            onClick={() => setShowTriggerModal(false)}
                            className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleTriggerReports}
                            disabled={triggerLoading}
                            className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                        >
                            <PaperAirplaneIcon className={`h-4 w-4 ${triggerLoading ? 'animate-spin' : ''}`} />
                            <span>{triggerLoading ? 'Sending...' : 'Send Reports'}</span>
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const ReportDetailPanel = ({ log, user, company, formatDate }) => {
    return (
        <div className="space-y-6">
            {/* Recipient */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Recipient</h4>
                <div className="bg-gray-700 p-4">
                    <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center">
                            {user?.avatar ? (
                                <img src={user.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                                <UserIcon className="h-5 w-5 text-gray-400" />
                            )}
                        </div>
                        <div>
                            <div className="text-white font-medium">{log.recipientEmail}</div>
                            {user && (
                                <div className="text-gray-400 text-sm">
                                    {user.firstName} {user.lastName}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Company */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Company</h4>
                <div className="bg-gray-700 p-4">
                    <div className="flex items-center space-x-4">
                        <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
                        <div>
                            <div className="text-white font-medium">
                                {company?.name || 'Unknown Company'}
                            </div>
                            <div className="text-gray-400 text-sm font-mono">
                                {log.companyId}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Details */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Report Details</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-gray-400 text-sm">Email Mode</span>
                            <div className="mt-1">
                                <StatusBadge status={log.emailMode?.toLowerCase().replace(/_/g, ' ')} />
                            </div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Saved Search</span>
                            <div className="text-white mt-1">
                                {log.savedSearchName || <span className="text-gray-500 italic">-</span>}
                            </div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Tenders Included</span>
                            <div className="text-white mt-1 text-lg font-semibold">{log.tendersIncluded}</div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Searches Included</span>
                            <div className="text-white mt-1 text-lg font-semibold">{log.searchesIncluded}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timing */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Timing</h4>
                <div className="bg-gray-700 p-4">
                    <div className="flex items-center space-x-2">
                        <ClockIcon className="h-5 w-5 text-gray-400" />
                        <div>
                            <div className="text-white">{formatDate(log.sentAt)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Job Info */}
            {log.jobId && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Job ID</h4>
                    <div className="bg-gray-700 p-4">
                        <code className="text-gray-300 text-sm break-all">{log.jobId}</code>
                    </div>
                </div>
            )}

            {/* Debug IDs */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Debug IDs</h4>
                <div className="bg-gray-700 p-4 space-y-2 text-xs font-mono">
                    <div className="flex justify-between">
                        <span className="text-gray-400">Log ID:</span>
                        <span className="text-gray-300">{log.id}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-400">User ID:</span>
                        <span className="text-gray-300">{log.userId}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-400">Company ID:</span>
                        <span className="text-gray-300">{log.companyId}</span>
                    </div>
                    {log.savedSearchId && (
                        <div className="flex justify-between">
                            <span className="text-gray-400">Saved Search ID:</span>
                            <span className="text-gray-300">{log.savedSearchId}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SavedSearchReports;
